<script setup lang="ts">
/**
 * CampaignDonorsList — feed "En direct" des donateurs : tri Récents / Top dons,
 * highlight du top donateur, animation d'arrivée des nouveaux dons, pagination.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps malgré leurs thèmes différents.
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignDonorsList from "@donoor/ui/campaign/CampaignDonorsList.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { CampaignDonor, TopDonor } from "./types";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`,
// présentes dans donoor.org ET dashboard.donoor.org (useI18n auto-importé).
const { t } = useI18n();

const props = defineProps<{
  donors: CampaignDonor[];
  donorsTotal: number;
  topDonor: TopDonor | null;
  sortBy: "recent" | "highest";
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  formatAmountShort: (amount: number) => string;
  getRelativeTime: (dateString: string) => string;
}>();

const emit = defineEmits<{
  "update:sortBy": [value: "recent" | "highest"];
  loadMore: [];
}>();

const recentlyAddedIds = ref<Set<string>>(new Set());

watch(
  () => props.donors[0]?.id,
  (newFirstId, oldFirstId) => {
    if (newFirstId && newFirstId !== oldFirstId && props.sortBy === "recent") {
      recentlyAddedIds.value.add(newFirstId);
      setTimeout(() => {
        recentlyAddedIds.value.delete(newFirstId);
      }, 3000);
    }
  },
);

function isRecentlyAdded(donorId: string): boolean {
  return recentlyAddedIds.value.has(donorId);
}

// ─── Scroll infini ───────────────────────────────────────────────────
// Un IntersectionObserver surveille une sentinelle en bas de la liste
// scrollable : dès qu'elle entre dans le viewport de la zone scrollable et
// qu'il reste des donateurs à charger, on émet `loadMore` — plus besoin du
// bouton « Afficher plus ». On garde `hasMore`/`isLoadingMore`/`@loadMore`
// inchangés → les consommateurs (dashboard preview) fonctionnent tel quel.
const scrollRoot = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

function maybeLoadMore() {
  if (props.hasMore && !props.isLoadingMore) {
    emit("loadMore");
  }
}

onMounted(() => {
  if (typeof IntersectionObserver === "undefined") return;
  // root = le conteneur scrollable (`max-h-80 overflow-y-auto`), PAS le
  // viewport : la sentinelle défile à l'intérieur de cette zone. rootMargin
  // bas de 200px → charge la page suivante un peu avant d'atteindre le fond,
  // pour un scroll continu sans à-coup.
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) maybeLoadMore();
    },
    { root: scrollRoot.value, rootMargin: "0px 0px 200px 0px" },
  );
  watch(
    sentinel,
    (el, _prev, onCleanup) => {
      if (!observer || !el) return;
      observer.observe(el);
      onCleanup(() => observer?.unobserve(el));
    },
    { immediate: true },
  );
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-[32px] overflow-hidden border border-slate-300 dark:border-gray-700"
  >
    <div
      class="px-4 xs:px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-nowrap items-center justify-between gap-3"
    >
      <div class="flex items-center gap-2 min-w-0 shrink-0">
        <span class="live-dot relative flex h-3 w-3 shrink-0">
          <span
            class="live-dot-ping animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1f9afe] opacity-75"
          />
          <span
            class="relative inline-flex rounded-full h-3 w-3 bg-[#1f9afe]"
          />
        </span>
        <span class="font-semibold text-gray-900 dark:text-white whitespace-nowrap">
          <span class="hidden xs:inline">{{ t("ui.donorsList.enDirectComplet") }}</span
          ><span class="xs:hidden">{{ t("ui.donorsList.enDirectCourt") }}</span></span
        >
        <span class="text-xs text-gray-500 dark:text-gray-400 shrink-0"
          >({{ donorsTotal }})</span
        >
      </div>

      <div
        class="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1 ring-1 ring-gray-200 dark:ring-gray-700 shrink-0"
      >
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-full transition-all"
          :class="
            sortBy === 'recent'
              ? 'bg-white text-[#0F3D8C] shadow-sm ring-1 ring-gray-200'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="emit('update:sortBy', 'recent')"
        >
          <UIcon name="i-lucide-clock-3" class="size-3.5 hidden xs:inline-flex" />
          {{ t("ui.donorsList.recents") }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-full transition-all"
          :class="
            sortBy === 'highest'
              ? 'bg-white text-[#0F3D8C] shadow-sm ring-1 ring-gray-200'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="emit('update:sortBy', 'highest')"
        >
          <UIcon name="i-lucide-trending-up" class="size-3.5 hidden xs:inline-flex" />
          {{ t("ui.commun.topDons") }}
        </button>
      </div>
    </div>

    <div
      ref="scrollRoot"
      class="divide-y divide-gray-100 dark:divide-gray-700 max-h-80 overflow-y-auto"
    >
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <UIcon
          name="i-lucide-loader-2"
          class="size-6 animate-spin text-[#1f9afe]"
        />
      </div>

      <template v-else>
        <TransitionGroup name="donor-list" tag="div" class="flex flex-col">
          <div
            v-for="donor in donors"
            :key="donor.id"
            class="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            :class="[
              donor.id === topDonor?.id
                ? 'bg-amber-50/50 dark:bg-amber-900/10'
                : '',
              isRecentlyAdded(donor.id) ? 'new-donation-highlight' : '',
            ]"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <div class="relative shrink-0">
                <div
                  class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                  :class="[
                    donor.id === topDonor?.id
                      ? 'bg-linear-to-br from-amber-400 to-yellow-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
                    isRecentlyAdded(donor.id)
                      ? 'ring-2 ring-[#1f9afe] ring-offset-2 scale-110'
                      : '',
                  ]"
                >
                  {{
                    donor.is_anonymous
                      ? "?"
                      : (donor.name ?? "").charAt(0).toUpperCase()
                  }}
                </div>
                <div
                  v-if="donor.id === topDonor?.id"
                  class="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center"
                >
                  <UIcon name="i-lucide-crown" class="size-2.5 text-white" />
                </div>
                <div
                  v-if="isRecentlyAdded(donor.id)"
                  class="absolute inset-0 rounded-full bg-[#1f9afe] animate-ping opacity-40"
                />
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="font-medium text-gray-900 dark:text-white text-sm">
                    {{ donor.is_anonymous ? t("ui.commun.anonyme") : donor.name }}
                  </p>
                  <span
                    v-if="isRecentlyAdded(donor.id)"
                    class="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-[#1f9afe] text-white rounded animate-pulse"
                  >
                    {{ t("ui.donorsList.nouveau") }}
                  </span>
                  <span
                    v-else-if="donor.id === topDonor?.id"
                    class="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-amber-500 text-white rounded"
                  >
                    {{ t("ui.donorsList.top") }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ getRelativeTime(donor.created_at) }}
                </p>
                <p
                  v-if="donor.message"
                  class="mt-1 text-xs text-gray-600 dark:text-gray-300 italic line-clamp-2"
                >
                  "{{ donor.message }}"
                </p>
              </div>
            </div>
            <span
              class="font-bold transition-all duration-300 shrink-0"
              :class="[
                donor.id === topDonor?.id
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-[#1f9afe]',
                isRecentlyAdded(donor.id) ? 'text-lg scale-110' : '',
              ]"
            >
              + {{ formatAmountShort(donor.amount) }}
            </span>
          </div>
        </TransitionGroup>

        <!-- Sentinelle scroll infini : quand elle entre dans la zone
             scrollable, l'IntersectionObserver déclenche `loadMore`. On la
             garde hors du flux visuel (h-px) ; le spinner ci-dessous fait le
             feedback. -->
        <div v-if="hasMore" ref="sentinel" class="h-px w-full" aria-hidden="true" />

        <!-- Feedback de chargement de la page suivante -->
        <div
          v-if="isLoadingMore"
          class="flex items-center justify-center py-4"
        >
          <UIcon
            name="i-lucide-loader-2"
            class="size-5 animate-spin text-[#1f9afe]"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* ─── Point « En direct » : anti-saccade mobile ──────────────────────────
   Le `animate-ping` (scale + fade en boucle) provoquait un repaint à chaque
   frame → saccade visible sur mobile, surtout quand la liste défile à côté.
   On isole le point sur sa propre couche GPU (`translateZ(0)` + `will-change`)
   pour que l'anim soit composée sans repeindre le header. `backface-visibility`
   évite un flash de sous-pixel sur iOS. */
.live-dot {
  transform: translateZ(0);
}
.live-dot-ping {
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* ─── Animation d'arrivée d'un nouveau donateur (TransitionGroup) ─────────
   Le nouveau donateur slide depuis le haut avec un fade + scale subtil,
   et les items existants se décalent vers le bas en douceur. Pattern
   utilisé chez Linear / Stripe Dashboard / GoFundMe pour les feeds live. */

.donor-list-enter-active,
.donor-list-leave-active {
  transition:
    opacity 400ms cubic-bezier(0.32, 0.72, 0, 1),
    transform 400ms cubic-bezier(0.32, 0.72, 0, 1),
    background-color 800ms ease;
}

/* Déplacement des items existants (FLIP automatique) */
.donor-list-move {
  transition: transform 400ms cubic-bezier(0.32, 0.72, 0, 1);
}

.donor-list-enter-from {
  opacity: 0;
  transform: translateY(-12px) scale(0.96);
}
.donor-list-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.donor-list-leave-active {
  position: absolute;
  width: 100%;
}
.donor-list-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

/* ─── Highlight "nouveau don" (flash bleu Donoor qui s'estompe en 2s) ─── */
.new-donation-highlight {
  animation: new-donation-flash 2s cubic-bezier(0.32, 0.72, 0, 1) forwards;
}
@keyframes new-donation-flash {
  0% {
    background-color: rgba(31, 154, 254, 0.16);
    box-shadow: inset 3px 0 0 #1f9afe;
  }
  50% {
    background-color: rgba(31, 154, 254, 0.08);
  }
  100% {
    background-color: transparent;
    box-shadow: inset 3px 0 0 transparent;
  }
}
</style>
